/* Farmacias LF · Listado de colección y búsqueda (sistema Cápsula).
   Mejora progresiva sobre formularios GET y enlaces normales (todo funciona sin JS):
   - Escritorio (≥ 990 px): cada cambio de filtro u orden recarga solo la sección (Section Rendering API).
   - Móvil y tablet: en la hoja de filtros, cada cambio actualiza el botón «Ver N productos»; al tocarlo se aplica.
   - Chips de filtros activos y «Limpiar» sin recargar la página.
   - «Ver más» agrega la página siguiente a la grilla; los números de página siguen siendo enlaces.
   - La barra «Filtrar | Ordenar» se acomoda bajo el header fijo (variable --lf-sticky-top).
   Marcado: sections/lf-main-collection.liquid, sections/lf-main-search.liquid, snippets/lf-filtros.liquid,
   snippets/lf-paginacion.liquid. Paneles: assets/lf-capsula.js (window.LF).
*/
(() => {
  if (window.LFListado) return;

  const html = document.documentElement;
  html.classList.add('lf-listado-js');

  const desktop = window.matchMedia('(min-width: 990px)');
  const cache = new Map();
  let lastUrl = location.pathname + location.search;
  let previewSeq = 0;
  let previewTimer = null;

  const rootOf = (el) => (el && el.closest ? el.closest('[data-lf-listado]') : null);

  const sectionUrl = (url, id) => {
    const u = new URL(url, location.href);
    u.searchParams.delete('section_id');
    u.searchParams.set('section_id', id);
    return u.toString();
  };

  const cleanUrl = (url) => {
    const u = new URL(url, location.href);
    u.searchParams.delete('section_id');
    return u.pathname + u.search;
  };

  const fetchHTML = (url, id) => {
    const key = sectionUrl(url, id);
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    return fetch(key, { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cache.size > 30) cache.clear();
        cache.set(key, text);
        return text;
      });
  };

  const parseRoot = (text) => {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const root = doc.querySelector('[data-lf-listado]');
    if (!root) throw new Error('Sin listado');
    return document.importNode(root, true);
  };

  const announce = (root, text) => {
    const el = document.getElementById(`lf-status-${root.dataset.lfListado}`);
    if (!el || !text) return;
    el.textContent = '';
    setTimeout(() => { el.textContent = text; }, 60);
  };

  const urlFromForm = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (typeof value !== 'string') return;
      const v = value.trim();
      if (v !== '') params.append(key, v);
    });
    const action = new URL(form.getAttribute('action') || location.pathname, location.href);
    const qs = params.toString();
    return action.pathname + (qs ? `?${qs}` : '');
  };

  const stickyTop = () => parseFloat(getComputedStyle(html).getPropertyValue('--lf-sticky-top')) || 0;

  const scrollToResults = (root) => {
    const target = root.querySelector('[data-lf-results]') || root;
    const top = target.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.6) {
      window.scrollTo({ top: Math.max(0, top + window.scrollY - stickyTop() - 8), behavior: 'auto' });
    }
  };

  /* Reemplaza el listado completo por la versión del servidor */
  const navigate = (root, url, { push = true, scroll = false } = {}) => {
    if (!root) {
      location.href = url;
      return Promise.resolve();
    }
    const id = root.dataset.lfListado;
    const target = cleanUrl(url);
    root.classList.add('lf-is-loading');
    root.setAttribute('aria-busy', 'true');
    clearTimeout(previewTimer);

    return fetchHTML(target, id)
      .then((text) => {
        const fresh = parseRoot(text);
        const panelOpen = !!root.querySelector('.lf-panel.lf-is-open');
        const active = document.activeElement;
        const activeId = active && root.contains(active) ? active.id : '';
        const closed = Array.from(root.querySelectorAll('details[data-lf-group]:not([open])')).map((d) => d.id);

        if (panelOpen && window.LF) window.LF.closeAll();
        root.replaceWith(fresh);

        closed.forEach((gid) => {
          const d = gid && document.getElementById(gid);
          if (d) d.removeAttribute('open');
        });

        if (push && target !== lastUrl) history.pushState({ lfListado: id }, '', target);
        lastUrl = target;

        if (panelOpen) {
          const opener = fresh.querySelector('[data-lf-open]');
          if (opener) opener.focus({ preventScroll: true });
        } else if (activeId) {
          const again = document.getElementById(activeId);
          if (again) again.focus({ preventScroll: true });
        }
        if (scroll) scrollToResults(fresh);
        announce(fresh, fresh.dataset.lfStatus);
      })
      .catch(() => {
        location.href = target;
      });
  };

  /* Móvil: actualiza «Ver N productos» sin cerrar la hoja */
  const preview = (form) => {
    const root = rootOf(form);
    const btn = form.querySelector('[data-lf-apply]');
    if (!root || !btn) return;
    clearTimeout(previewTimer);
    previewTimer = setTimeout(() => {
      const seq = ++previewSeq;
      btn.classList.add('lf-is-loading');
      fetchHTML(urlFromForm(form), root.dataset.lfListado)
        .then((text) => {
          if (seq !== previewSeq) return;
          const n = parseInt(parseRoot(text).dataset.count || '0', 10) || 0;
          const label = n === 0 ? btn.dataset.zero
            : n === 1 ? btn.dataset.one
            : (btn.dataset.many || '').replace('[n]', n.toLocaleString('es-CL'));
          if (label) btn.textContent = label;
        })
        .catch(() => {})
        .finally(() => {
          if (seq === previewSeq) btn.classList.remove('lf-is-loading');
        });
    }, 300);
  };

  /* «Ver más»: agrega la página siguiente a la grilla */
  const loadMore = (link) => {
    const root = rootOf(link);
    if (!root || link.classList.contains('lf-is-loading')) return;
    const grid = root.querySelector('[data-lf-grid]');
    const pager = link.closest('[data-lf-pager]');
    if (!grid || !pager) {
      location.href = link.href;
      return;
    }
    link.classList.add('lf-is-loading');
    link.setAttribute('aria-busy', 'true');
    const target = cleanUrl(link.href);

    fetchHTML(target, root.dataset.lfListado)
      .then((text) => {
        const next = parseRoot(text);
        const nextGrid = next.querySelector('[data-lf-grid]');
        const nextPager = next.querySelector('[data-lf-pager]');
        if (!nextGrid) throw new Error('Sin grilla');
        const items = Array.from(nextGrid.children).filter((li) => !li.classList.contains('lf-pgrid__ayuda'));
        items.forEach((li) => grid.appendChild(li));
        const msg = (pager.dataset.msg || '').replace('[n]', String(items.length));

        /* Búsqueda: páginas y artículos de la página siguiente */
        const nextOtros = next.querySelector('.lf-sotros');
        if (nextOtros) {
          const otrosList = root.querySelector('.lf-sotros ul');
          if (otrosList) nextOtros.querySelectorAll('ul > li').forEach((li) => otrosList.appendChild(li));
          else pager.before(nextOtros);
        }

        if (nextPager) {
          const first = pager.dataset.first || '1';
          const last = nextPager.dataset.last || '';
          const total = parseInt(nextPager.dataset.total || '0', 10) || 0;
          nextPager.dataset.first = first;
          const range = nextPager.querySelector('[data-lf-range]');
          if (range) range.textContent = `${first}–${last}`;
          const bar = nextPager.querySelector('[data-lf-bar]');
          if (bar && total > 0) bar.style.width = `${Math.min(100, Math.round((parseInt(last, 10) || 0) * 100 / total))}%`;
          pager.replaceWith(nextPager);
        } else {
          pager.remove();
        }

        history.replaceState(history.state, '', target);
        lastUrl = target;

        const firstLink = items[0] && items[0].querySelector('.lf-name a, a[href]');
        if (firstLink) firstLink.focus({ preventScroll: true });
        announce(root, msg);
      })
      .catch(() => {
        location.href = target;
      });
  };

  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  /* ---------------- Eventos (delegados: sobreviven al reemplazo del listado) ---------------- */

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (t.matches('[data-lf-sort]')) {
      const root = rootOf(t);
      if (!root) return;
      const u = new URL(location.href);
      u.searchParams.set('sort_by', t.value);
      u.searchParams.delete('page');
      navigate(root, u.pathname + u.search);
      return;
    }

    if (t.matches('[data-lf-fsearch], [data-lf-q]')) return;
    const form = t.closest('form[data-lf-filtros]');
    if (!form) return;
    if (desktop.matches) navigate(rootOf(form), urlFromForm(form));
    else preview(form);
  });

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-lf-filtros]')) return;
    e.preventDefault();
    navigate(rootOf(form), urlFromForm(form), { scroll: !desktop.matches });
  });

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;

    const ajax = t.closest('a[data-lf-ajax]');
    if (ajax) {
      const root = rootOf(ajax);
      if (!root) return;
      e.preventDefault();
      navigate(root, ajax.href, { scroll: true });
      return;
    }

    const more = t.closest('a[data-lf-more]');
    if (more) {
      e.preventDefault();
      loadMore(more);
      return;
    }

    const clear = t.closest('[data-lf-clear-q]');
    if (clear) {
      const input = clear.closest('form') && clear.closest('form').querySelector('[data-lf-q]');
      if (input) {
        input.value = '';
        input.focus();
      }
      clear.hidden = true;
    }
  });

  document.addEventListener('input', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (t.matches('[data-lf-q]')) {
      const clear = t.closest('form') && t.closest('form').querySelector('[data-lf-clear-q]');
      if (clear) clear.hidden = t.value === '';
      return;
    }

    if (t.matches('[data-lf-fsearch]')) {
      const group = t.closest('[data-lf-group]');
      if (!group) return;
      const q = norm(t.value.trim());
      group.querySelectorAll('li[data-lf-label]').forEach((li) => {
        li.hidden = q !== '' && !norm(li.dataset.lfLabel).includes(q);
      });
      const more = group.querySelector('details.lf-fmore');
      if (more && q !== '') more.open = true;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target instanceof Element && e.target.matches('[data-lf-fsearch]')) e.preventDefault();
  });

  window.addEventListener('popstate', () => {
    const current = location.pathname + location.search;
    if (current === lastUrl) return;
    const root = document.querySelector('[data-lf-listado]');
    if (!root) return;
    lastUrl = current;
    navigate(root, current, { push: false });
  });

  /* Al pasar a escritorio, la hoja se cierra y el panel vuelve a ser barra lateral */
  const onBreakpoint = () => {
    if (!desktop.matches) return;
    if (document.querySelector('.lf-filtros.lf-is-open') && window.LF) window.LF.closeAll();
    setTimeout(() => {
      document.querySelectorAll('.lf-filtros[aria-hidden]').forEach((p) => p.removeAttribute('aria-hidden'));
    }, 260);
  };
  if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
  else if (desktop.addListener) desktop.addListener(onBreakpoint);

  /* ---------------- Barra «Filtrar | Ordenar» bajo el header fijo ---------------- */
  let headers = [];
  let ticking = false;

  const findHeaders = () => {
    headers = Array.from(document.querySelectorAll(
      '.shopify-section-group-header-group, .shopify-section-group-header-group > *, .shopify-section-group-header-group > * > *'
    )).filter((el) => {
      const pos = getComputedStyle(el).position;
      return pos === 'sticky' || pos === 'fixed';
    });
  };

  const measure = () => {
    ticking = false;
    if (!document.querySelector('[data-lf-toolbar]')) return;
    let bottom = 0;
    headers.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.height > 0 && r.top <= 1 && r.bottom > bottom) bottom = r.bottom;
    });
    html.style.setProperty('--lf-sticky-top', `${Math.max(0, Math.round(bottom))}px`);
  };

  const schedule = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(measure);
  };

  const initSticky = () => {
    findHeaders();
    schedule();
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', initSticky);
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('shopify:section:load', initSticky);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSticky);
  else initSticky();

  window.LFListado = { navigate, loadMore };
})();
